// Browser-side implementation of the Electron preload Api. Talks to the
// dev server (server/index.ts) over fetch + a singleton WebSocket. Installed
// onto window.api when the renderer detects there's no Electron preload.
import type { Api } from '../../../shared/api';
import type {
  AgentChatRequest,
  AgentChatResponse,
  AgentConfig,
  ChatChunk,
  LayerCachePayload,
  PortalInfo,
  TokenInfo,
  ToolCall,
  ToolResult,
} from '../../../shared/ipc';
import { apiPath, wsUrl as buildWsUrl } from './base-url';

type ChunkHandler = (chunk: ChatChunk) => void;
type ToolCallHandler = (call: ToolCall) => void;
type ChatAbortedHandler = () => void;

const chunkHandlers = new Set<ChunkHandler>();
const toolCallHandlers = new Set<ToolCallHandler>();
const chatAbortedHandlers = new Set<ChatAbortedHandler>();

type PendingChat = {
  resolve: (resp: AgentChatResponse) => void;
  reject: (err: Error) => void;
};
const pendingChats = new Map<string, PendingChat>();

let socket: WebSocket | null = null;
let socketReady: Promise<WebSocket> | null = null;

function wsUrl(): string {
  return buildWsUrl('/ws/agent');
}

function ensureSocket(): Promise<WebSocket> {
  if (socket && socket.readyState === WebSocket.OPEN) return Promise.resolve(socket);
  if (socketReady) return socketReady;

  socketReady = new Promise<WebSocket>((resolve, reject) => {
    const ws = new WebSocket(wsUrl());
    socket = ws;
    ws.addEventListener('open', () => resolve(ws), { once: true });
    ws.addEventListener('error', (ev) => {
      reject(new Error(`WebSocket error connecting to ${wsUrl()}`));
      console.error('[web-api] ws error', ev);
    });
    ws.addEventListener('close', () => {
      socket = null;
      socketReady = null;
      // Reject any in-flight chat request so the UI exits its sending state.
      for (const [, p] of pendingChats) p.reject(new Error('WebSocket closed'));
      pendingChats.clear();
    });
    ws.addEventListener('message', (ev) => {
      const msg = parseJson(ev.data);
      if (!msg) return;
      switch (msg.kind) {
        case 'chunk':
          for (const h of chunkHandlers) h(msg.chunk);
          return;
        case 'tool-call':
          for (const h of toolCallHandlers) h(msg.call);
          return;
        case 'aborted':
          for (const h of chatAbortedHandlers) h();
          return;
        case 'done': {
          const pending = pendingChats.get(msg.reqId);
          if (!pending) return;
          pendingChats.delete(msg.reqId);
          if (msg.ok) pending.resolve(msg.response);
          else pending.reject(new Error(msg.error || 'chat failed'));
          return;
        }
      }
    });
  });
  return socketReady;
}

type ServerMessage =
  | { kind: 'chunk'; chunk: ChatChunk }
  | { kind: 'tool-call'; call: ToolCall }
  | { kind: 'aborted' }
  | { kind: 'done'; reqId: string; ok: true; response: AgentChatResponse }
  | { kind: 'done'; reqId: string; ok: false; error?: string; aborted?: boolean };

function parseJson(raw: unknown): ServerMessage | null {
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw) as ServerMessage;
  } catch {
    return null;
  }
}

async function getJson<T>(path: string): Promise<T> {
  const url = apiPath(path);
  const r = await fetch(url);
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json()) as T;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const url = apiPath(path);
  const r = await fetch(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json()) as T;
}

async function deleteReq<T>(path: string): Promise<T> {
  const url = apiPath(path);
  const r = await fetch(url, { method: 'DELETE' });
  if (!r.ok) throw new Error(`${url} → ${r.status}`);
  return (await r.json()) as T;
}

let chatSeq = 0;

const api: Api = {
  getArcgisToken: async (force = false): Promise<TokenInfo> => {
    return getJson<TokenInfo>(`/api/arcgis/token${force ? '?force=1' : ''}`);
  },

  getArcgisPortalInfo: () => getJson<PortalInfo>('/api/arcgis/portal-info'),

  layerCacheRead: async (layerId) => {
    const { value } = await getJson<{ value: LayerCachePayload | null }>(
      `/api/layers/cache/${encodeURIComponent(layerId)}`,
    );
    return value;
  },
  layerCacheWrite: async (layerId, payload) => {
    await postJson<{ ok: true }>(
      `/api/layers/cache/${encodeURIComponent(layerId)}`,
      payload,
    );
  },
  layerCacheInvalidate: async (layerId) => {
    const { invalidated } = await deleteReq<{ invalidated: boolean }>(
      `/api/layers/cache/${encodeURIComponent(layerId)}`,
    );
    return invalidated;
  },
  layerNotesRead: async (layerId) => {
    const { value } = await getJson<{ value: string | null }>(
      `/api/layers/notes/${encodeURIComponent(layerId)}`,
    );
    return value;
  },

  getAgentConfig: () => getJson<AgentConfig>('/api/agent/config'),

  chat: async (req: AgentChatRequest) => {
    const ws = await ensureSocket();
    chatSeq += 1;
    const reqId = `r_${Date.now().toString(36)}_${chatSeq.toString(36)}`;
    return new Promise<AgentChatResponse>((resolve, reject) => {
      pendingChats.set(reqId, { resolve, reject });
      ws.send(JSON.stringify({ kind: 'chat', reqId, req }));
    });
  },
  chatAbort: () => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ kind: 'abort' }));
    }
  },
  onChatAborted: (handler) => {
    chatAbortedHandlers.add(handler);
    return () => {
      chatAbortedHandlers.delete(handler);
    };
  },
  onChatChunk: (handler) => {
    chunkHandlers.add(handler);
    return () => {
      chunkHandlers.delete(handler);
    };
  },
  onToolCall: (handler) => {
    toolCallHandlers.add(handler);
    return () => {
      toolCallHandlers.delete(handler);
    };
  },
  toolResult: (result: ToolResult) => {
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({ kind: 'tool-result', result }));
    }
  },

  // Window controls have no effect in the browser.
  windowMinimize: () => {},
  windowToggleMaximize: () => {},
  windowClose: () => {},
  windowIsMaximized: async () => false,
  onWindowMaximizeState: () => () => {},
};

export function installWebApi(): void {
  if (typeof window === 'undefined') return;
  if ((window as unknown as { api?: unknown }).api) return;
  (window as unknown as { api: Api }).api = api;
  (window as unknown as { __platform__: string }).__platform__ = 'web';
}
